import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiHome, FiMap, FiChevronDown, FiGlobe, FiSun, FiMoon, FiThermometer, FiWind, FiSearch, FiRefreshCw, FiShare2 } from 'react-icons/fi';
import { WiDaySunnyOvercast } from 'react-icons/wi';
import { ThemeContext } from '../../contexts/ThemeContext';
import { TAKE_OFF_SITES, ALL_SITES } from '../../constants';
import { getNow } from '../../services/api';
import { useDashboardActions } from '../../contexts/DashboardActionsContext';
import { getFlightSafetyStatus } from '../../utils/flightSafety';
import { useStationSectors } from '../../contexts/StationSectorsContext';

const NavigationHeader = ({ onSearchOpen }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { theme, setTheme } = useContext(ThemeContext);
    const isDarkMode = theme === 'dark';
    const toggleTheme = () => setTheme(isDarkMode ? 'light' : 'dark');
    const { onRefresh, onShare, isRefreshing } = useDashboardActions();
    const [showSiteDropdown, setShowSiteDropdown] = useState(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [siteWeatherData, setSiteWeatherData] = useState({});
    const { byId: sectorsById } = useStationSectors();
    
    const siteDropdownRef = useRef(null);
    const languageDropdownRef = useRef(null);

    const currentSite = location.pathname.includes('/site/') 
        ? ALL_SITES.find(site => location.pathname.includes(site.slug))
        : null;

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (siteDropdownRef.current && !siteDropdownRef.current.contains(event.target)) {
                setShowSiteDropdown(false);
            }
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
                setShowLanguageDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Prevent background scroll when mobile menu is open
    useEffect(() => {
        if (showMobileMenu) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function to restore scroll when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showMobileMenu]);

    // Fetch weather data when either Sites dropdown or mobile menu opens —
    // both display TAKE_OFF_SITES and need the offline status for sorting.
    useEffect(() => {
        if (!showSiteDropdown && !showMobileMenu) return;
        TAKE_OFF_SITES.forEach(async (site) => {
            if (!siteWeatherData[site.id]) {
                try {
                    const weatherData = await getNow(site.id);
                    setSiteWeatherData(prev => ({
                        ...prev,
                        [site.id]: weatherData
                    }));
                } catch (error) {
                    console.error(`Failed to fetch weather for site ${site.id}:`, error);
                }
            }
        });
    }, [showSiteDropdown, showMobileMenu, siteWeatherData]);

    // Clear cached weather when the user returns to the tab so the next
    // dropdown open refetches fresh data (cached SEND_TIME would otherwise
    // be stale and mark all stations offline).
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                setSiteWeatherData({});
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('lang', lang);
        setShowLanguageDropdown(false);
    };

    const sortedSites = useMemo(() => {
        return [...TAKE_OFF_SITES].sort((a, b) => {
            const aOffline = getFlightSafetyStatus(a, siteWeatherData[a.id], sectorsById[a.id]).isOffline;
            const bOffline = getFlightSafetyStatus(b, siteWeatherData[b.id], sectorsById[b.id]).isOffline;
            if (aOffline && !bOffline) return 1;
            if (!aOffline && bOffline) return -1;
            return 0;
        });
    }, [siteWeatherData, sectorsById]);

    const renderWeatherInfo = (site) => {
        const weather = siteWeatherData[site.id];
        if (!weather) {
            return (
                <div className="flex items-center space-x-2 text-xs text-gray-400 dark:text-gray-500">
                    <span>Loading...</span>
                </div>
            );
        }

        const flightStatus = getFlightSafetyStatus(site, weather, sectorsById[site.id]);
        if (flightStatus.isOffline) {
            return (
                <div className="flex items-center text-xs text-red-600 dark:text-red-400">
                    <span>{t('Station Offline')}</span>
                </div>
            );
        }

        return (
            <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                    <FiThermometer className="w-3 h-3" />
                    <span>{Math.round(weather.TEMPERATURE || 0)}°C</span>
                </div>
                <div className="flex items-center space-x-1">
                    <FiWind className="w-3 h-3" />
                    <span>{Math.round(weather.WIND_SP || 0)} {t('m/s')}</span>
                </div>
            </div>
        );
    };

    const currentLanguage = i18n.language || 'en';
    const languages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'src', name: 'Српски', flag: '🇷🇸' },
        { code: 'sr', name: 'Srpski', flag: '🇷🇸' }
    ];

    return (
        <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-sm backdrop-blur-sm bg-white/95 dark:bg-slate-900/95">
            <div className="w-full px-3 sm:px-4 lg:px-6">
                <div className="flex justify-between items-center h-14 sm:h-16">
                    {/* Left group: Logo + Desktop Navigation */}
                    <div className="flex items-center gap-2">
                        {/* Logo and Brand */}
                        <Link to="/" className="flex items-center group">
                            <div className="relative">
                                <img
                                    src="/assets/images/logo.png"
                                    alt="VSS Logo"
                                    className="h-8 sm:h-10 w-auto max-w-none object-contain dark:invert dark:brightness-0"
                                    onError={(e) => {
                                        // Fallback if logo doesn't load
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div
                                    className="hidden h-10 w-12 bg-blue-600 rounded-lg items-center justify-center text-white font-bold text-lg"
                                    style={{display: 'none'}}
                                >
                                    VSS
                                </div>
                            </div>
                        </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-2">
                        {/* Home Link */}
                        <Link 
                            to="/" 
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                ${location.pathname === '/' 
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm' 
                                    : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <FiHome className="w-4 h-4" />
                            <span>{t('Home')}</span>
                        </Link>

                        {/* Forecast Link */}
                        <Link
                            to="/forecast"
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                ${location.pathname.startsWith('/forecast')
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm'
                                    : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <WiDaySunnyOvercast className="w-5 h-5" />
                            <span>{t('Forecast')}</span>
                        </Link>

                        {/* Site Selector Dropdown */}
                        <div className="relative" ref={siteDropdownRef}>
                            <button
                                onClick={() => setShowSiteDropdown(!showSiteDropdown)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                    ${showSiteDropdown || currentSite 
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm' 
                                        : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <FiMap className="w-4 h-4" />
                                <span className="max-w-32 truncate">
                                    {currentSite ? currentSite.name : t('Sites')}
                                </span>
                                <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${showSiteDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showSiteDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-xl shadow-xl py-2 border border-gray-200 dark:border-slate-700 animate-in slide-in-from-top-2">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-slate-700">
                                        {t('Takeoff Sites')}
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">
                                        {sortedSites.map(site => (
                                            <button
                                                key={site.slug}
                                                onClick={() => {
                                                    navigate(`/site/${site.slug}`);
                                                    setShowSiteDropdown(false);
                                                }}
                                                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors
                                                    ${currentSite?.slug === site.slug 
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-r-2 border-blue-500' 
                                                        : 'text-gray-700 dark:text-gray-300'
                                                    }`}
                                            >
                                                <div className="font-medium">{site.name}</div>
                                                <div className="mt-1">
                                                    {renderWeatherInfo(site)}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </nav>
                    </div>{/* end left group */}

                    {/* Right Side Actions */}
                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Search Button */}
                        <button
                            onClick={onSearchOpen}
                            className="p-2 sm:p-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
                            aria-label="Search takeoff sites"
                        >
                            <FiSearch className="w-4 h-4" />
                        </button>

                        {/* Refresh Button — only rendered when there's a handler */}
                        {onRefresh && (
                            <button
                                onClick={onRefresh}
                                disabled={isRefreshing}
                                className="p-2 sm:p-2.5 rounded-lg transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800"
                                aria-label={t('Refresh data')}
                                title={t('Refresh data')}
                            >
                                <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>
                        )}

                        {/* Share Button — only rendered when there's a handler */}
                        {onShare && (
                            <button
                                onClick={onShare}
                                className="hidden sm:block p-2 sm:p-2.5 rounded-lg transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800"
                                aria-label={t('Share')}
                                title={t('Share')}
                            >
                                <FiShare2 className="w-4 h-4" />
                            </button>
                        )}

                        {/* Language Dropdown - Desktop Only */}
                        <div className="relative hidden sm:block" ref={languageDropdownRef}>
                            <button
                                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
                                aria-label="Change language"
                            >
                                <FiGlobe className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                    {languages.find(lang => lang.code === currentLanguage)?.flag || '🌐'}
                                </span>
                                <FiChevronDown className={`w-3 h-3 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showLanguageDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl py-2 border border-gray-200 dark:border-slate-700 animate-in slide-in-from-top-2">
                                    {languages.map(lang => (
                                        <button
                                            key={lang.code}
                                            onClick={() => changeLanguage(lang.code)}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors
                                                ${currentLanguage === lang.code 
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                                                    : 'text-gray-700 dark:text-gray-300'
                                                }`}
                                        >
                                            <span className="mr-3">{lang.flag}</span>
                                            {lang.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Theme Toggle - Desktop Only */}
                        <button
                            onClick={toggleTheme}
                            className="hidden sm:block p-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
                            aria-label="Toggle theme"
                        >
                            {isDarkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
                        </button>

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                            className="md:hidden p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {showMobileMenu ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu — rendered via a PORTAL to document.body, not
                    inline in the header. The header has `backdrop-blur`
                    (backdrop-filter), which makes it the containing block for
                    fixed descendants — so a fixed menu *inside* the header
                    collapses to the header's 56px height. Portaling to body
                    escapes that, so `fixed ... top-14 bottom-0` is measured
                    against the viewport and the panel floats over the page.
                    Body scroll is locked while open (showMobileMenu effect). */}
                {showMobileMenu && createPortal(
                    <div className="md:hidden fixed inset-x-0 top-14 sm:top-16 bottom-0 z-40 py-4 border-t border-gray-200 dark:border-slate-700 overflow-y-auto bg-white dark:bg-slate-900">
                        <Link
                            to="/"
                            onClick={() => setShowMobileMenu(false)}
                            className="flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg mx-2"
                        >
                            <FiHome className="w-4 h-4" />
                            <span>{t('Home')}</span>
                        </Link>

                        <Link
                            to="/forecast"
                            onClick={() => setShowMobileMenu(false)}
                            className="flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg mx-2"
                        >
                            <WiDaySunnyOvercast className="w-5 h-5" />
                            <span>{t('Forecast')}</span>
                        </Link>

                        <div className="px-4 py-2 mt-3">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                {t('Sites')}
                            </div>
                            <div className="space-y-1">
                                {sortedSites.map(site => (
                                    <Link
                                        key={site.slug}
                                        to={`/site/${site.slug}`}
                                        onClick={() => setShowMobileMenu(false)}
                                        className={`block py-2 px-3 text-sm rounded-lg transition-colors
                                            ${currentSite?.slug === site.slug 
                                                ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20' 
                                                : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        {site.name}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 mt-4">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                {t('Language')}
                            </div>
                            <div className="space-y-1">
                                {languages.map(lang => (
                                    <button
                                        key={lang.code}
                                        onClick={() => {
                                            changeLanguage(lang.code);
                                            setShowMobileMenu(false);
                                        }}
                                        className={`w-full text-left py-2 px-3 text-sm rounded-lg transition-colors
                                            ${currentLanguage === lang.code 
                                                ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20' 
                                                : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        <span className="mr-3">{lang.flag}</span>
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Refresh / Share — only shown when handlers are available (station page) */}
                        {(onRefresh || onShare) && (
                            <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                    {t('Actions')}
                                </div>
                                {onRefresh && (
                                    <button
                                        onClick={() => { onRefresh(); setShowMobileMenu(false); }}
                                        disabled={isRefreshing}
                                        className="w-full flex items-center space-x-3 py-2 px-3 text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                        <span>{t('Refresh data')}</span>
                                    </button>
                                )}
                                {onShare && (
                                    <button
                                        onClick={() => { onShare(); setShowMobileMenu(false); }}
                                        className="w-full flex items-center space-x-3 py-2 px-3 text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                                    >
                                        <FiShare2 className="w-4 h-4" />
                                        <span>{t('Share')}</span>
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                {t('Theme')}
                            </div>
                            <button
                                onClick={() => {
                                    toggleTheme();
                                    setShowMobileMenu(false);
                                }}
                                className="w-full flex items-center space-x-3 py-2 px-3 text-sm rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                            >
                                {isDarkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
                                <span>{isDarkMode ? t('Light Mode') : t('Dark Mode')}</span>
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
            </div>

        </header>
    );
};

export default NavigationHeader;